import { useState, useEffect, useRef } from 'react';
import { DashboardLayout } from '../../components/DashboardLayout';
import { useAuth } from '../../contexts/AuthContext';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import {
  Heart, Calendar, FileText, Users, BookOpen, Pill, Image, MessageSquare,
  Clock, Send, User, Star, ChevronRight, Video, Edit3, Phone,
  MapPin, Radio, CheckCircle2, Circle, Navigation, AlertTriangle, Sparkles, RefreshCw, Smartphone
} from 'lucide-react';
import { SpeechToTextButton } from '../../components/SpeechToTextButton';
import { toast } from 'sonner';

import { Link } from 'react-router-dom';
const API_URL = process.env.REACT_APP_BACKEND_URL;

export function FamilyDashboard() {
  const { user } = useAuth();
  const [clientProfile, setClientProfile] = useState(null);
  const [photos, setPhotos] = useState([]);
  const [careReports, setCareReports] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [schedule, setSchedule] = useState([]);
  const [tracking, setTracking] = useState({ active_shifts: [] });
  const [modRequests, setModRequests] = useState([]);
  const [messages, setMessages] = useState([]);
  const [selectedContact, setSelectedContact] = useState(null);
  const [msgText, setMsgText] = useState('');
  const [activeTab, setActiveTab] = useState('overview');
  const [modForm, setModForm] = useState({ shift_id: '', request_type: 'modification', current_time: '', requested_time: '', reason: '' });
  const [dailySummaries, setDailySummaries] = useState([]);
  const [visitTimeline, setVisitTimeline] = useState([]);
  const [visitStats, setVisitStats] = useState({});
  const [loading, setLoading] = useState(true);
  const mapRef = useRef(null);
  const mapInstance = useRef(null);

  useEffect(() => {
    fetchAll();
  }, []);

  const fetchAll = async () => {
    try {
      const [profileRes, photosRes, reportsRes, contactsRes, schedRes, trackRes, modRes, summRes, visitRes] = await Promise.all([
        axios.get(`${API_URL}/api/family-portal/client-profile`, { withCredentials: true }).catch(() => ({ data: null })),
        axios.get(`${API_URL}/api/family-portal/photos`, { withCredentials: true }).catch(() => ({ data: [] })),
        axios.get(`${API_URL}/api/family-portal/care-reports`, { withCredentials: true }).catch(() => ({ data: [] })),
        axios.get(`${API_URL}/api/family-portal/contacts`, { withCredentials: true }).catch(() => ({ data: [] })),
        axios.get(`${API_URL}/api/family-portal/schedule`, { withCredentials: true }).catch(() => ({ data: [] })),
        axios.get(`${API_URL}/api/family-portal/care-tracking`, { withCredentials: true }).catch(() => ({ data: { active_shifts: [] } })),
        axios.get(`${API_URL}/api/family-portal/shift-modification-requests`, { withCredentials: true }).catch(() => ({ data: [] })),
        axios.get(`${API_URL}/api/family-portal/daily-summaries`, { withCredentials: true }).catch(() => ({ data: [] })),
        axios.get(`${API_URL}/api/family-portal/visit-timeline`, { withCredentials: true }).catch(() => ({ data: { timeline: [], stats: {} } })),
      ]);
      setClientProfile(profileRes.data);
      setPhotos(photosRes.data || []);
      setCareReports(reportsRes.data || []);
      setContacts(contactsRes.data || []);
      setSchedule(schedRes.data || []);
      setTracking(trackRes.data || { active_shifts: [] });
      setModRequests(modRes.data || []);
      setDailySummaries(summRes.data || []);
      setVisitTimeline(visitRes.data?.timeline || []);
      setVisitStats(visitRes.data?.stats || {});
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  };

  const fetchMessages = async (contactId) => {
    try {
      const { data } = await axios.get(`${API_URL}/api/family-portal/messages?partner_id=${contactId}`, { withCredentials: true });
      setMessages(data);
    } catch (err) { console.error(err); }
  };

  const sendMessage = async () => {
    if (!msgText.trim() || !selectedContact) return;
    try {
      await axios.post(`${API_URL}/api/family-portal/messages`, {
        recipient_id: selectedContact.id, content: msgText
      }, { withCredentials: true });
      setMsgText('');
      fetchMessages(selectedContact.id);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to send');
    }
  };

  const submitModRequest = async () => {
    if (!modForm.reason.trim()) { toast.error('Please provide a reason'); return; }
    try {
      await axios.post(`${API_URL}/api/family-portal/shift-modification-request`, modForm, { withCredentials: true });
      toast.success('Modification request submitted');
      setModForm({ shift_id: '', request_type: 'modification', current_time: '', requested_time: '', reason: '' });
      fetchAll();
    } catch (err) { toast.error('Failed to submit request'); }
  };

  // Map
  useEffect(() => {
    if (activeTab !== 'tracking' || !mapRef.current || mapInstance.current) return;
    if (typeof window.L === 'undefined') return;
    const L = window.L;
    const map = L.map(mapRef.current).setView([51.5074, -0.1278], 13);
    L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', { attribution: '&copy; OpenStreetMap' }).addTo(map);
    mapInstance.current = map;
    return () => { if (mapInstance.current) { mapInstance.current.remove(); mapInstance.current = null; } };
  }, [activeTab]);

  useEffect(() => {
    if (!mapInstance.current || typeof window.L === 'undefined') return;
    const L = window.L;
    const map = mapInstance.current;
    map.eachLayer(l => { if (l instanceof L.Marker) map.removeLayer(l); });
    const bounds = [];
    tracking.active_shifts.forEach(s => {
      if (s.latitude && s.longitude) {
        L.marker([s.latitude, s.longitude], { icon: L.divIcon({ className: '', html: '<div style="background:#00A6A0;width:12px;height:12px;border-radius:50%;border:2px solid white;"></div>', iconSize: [12, 12], iconAnchor: [6, 6] }) }).addTo(map).bindPopup(`<b>${s.address || 'Location'}</b>`);
        bounds.push([s.latitude, s.longitude]);
      }
      if (s.live_location?.latitude) {
        L.marker([s.live_location.latitude, s.live_location.longitude], { icon: L.divIcon({ className: '', html: '<div style="background:#FF6F61;width:14px;height:14px;border-radius:50%;border:2px solid white;"></div>', iconSize: [14, 14], iconAnchor: [7, 7] }) }).addTo(map).bindPopup(`<b>${s.caregiver_name}</b> - Live`);
        bounds.push([s.live_location.latitude, s.live_location.longitude]);
      }
    });
    if (bounds.length > 0) map.fitBounds(bounds, { padding: [30, 30], maxZoom: 15 });
  }, [tracking.active_shifts, activeTab]);

  const TABS = [
    { key: 'overview', label: 'Overview', icon: Heart },
    { key: 'visits', label: 'Visit Timeline', icon: MapPin },
    { key: 'ai-summary', label: 'Daily Summary', icon: Sparkles },
    { key: 'tracking', label: 'Care Tracking', icon: Radio },
    { key: 'photos', label: 'Photos', icon: Image },
    { key: 'reports', label: 'Care Reports', icon: FileText },
    { key: 'messages', label: 'Messages', icon: MessageSquare },
    { key: 'schedule', label: 'Schedule', icon: Calendar },
    { key: 'modifications', label: 'Request Change', icon: Edit3 },
  ];

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="family-dashboard">
        <div>
          <h1 className="text-2xl font-heading font-bold text-foreground">Family Portal</h1>
          <p className="text-sm text-muted-foreground mt-1">
            {clientProfile ? `Caring for ${clientProfile.name}` : 'Stay connected with your loved one\'s care'}
          </p>
        </div>

        {/* Tabs */}
        <div className="flex gap-2 overflow-x-auto scrollbar-hide pb-1">
          {TABS.map(t => {
            const Icon = t.icon;
            return (
              <Button key={t.key} variant={activeTab === t.key ? 'default' : 'outline'} size="sm"
                onClick={() => { setActiveTab(t.key); if (t.key === 'tracking') { mapInstance.current = null; } }}
                className={`flex-shrink-0 ${activeTab === t.key ? 'btn-primary' : 'border-border text-foreground hover:bg-muted'}`}
                data-testid={`tab-${t.key}`}>
                <Icon className="w-4 h-4 mr-1.5" /> {t.label}
              </Button>
            );
          })}
        </div>

        {/* OVERVIEW TAB */}
        {activeTab === 'overview' && (
          <div className="space-y-6">
            {/* Client Profile Card */}
            {clientProfile && (
              <Card className="glass-card border-border">
                <CardHeader className="border-b border-border">
                  <CardTitle className="text-lg font-heading text-foreground flex items-center gap-2">
                    <User className="w-5 h-5 text-[#00A6A0]" /> {clientProfile.name}
                  </CardTitle>
                </CardHeader>
                <CardContent className="p-4 grid sm:grid-cols-2 gap-3">
                  {clientProfile.phone && <p className="text-sm text-muted-foreground flex items-center gap-2"><Phone className="w-4 h-4 text-[#00A6A0]" />{clientProfile.phone}</p>}
                  {clientProfile.address && <p className="text-sm text-muted-foreground flex items-center gap-2"><MapPin className="w-4 h-4 text-[#00A6A0]" />{clientProfile.address}</p>}
                  {clientProfile.medical_notes && (
                    <div className="col-span-2 p-3 rounded-lg bg-red-500/5 border border-red-500/20">
                      <p className="text-xs text-red-400 font-medium flex items-center gap-1 mb-1"><AlertTriangle className="w-3 h-3" /> Medical Notes</p>
                      <p className="text-xs text-muted-foreground">{clientProfile.medical_notes}</p>
                    </div>
                  )}
                </CardContent>
              </Card>
            )}

            {/* Quick Stats */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
              <Card className="glass-card border-border"><CardContent className="p-4 text-center">
                <p className="text-2xl font-bold text-[#00A6A0]">{tracking.active_shifts.length}</p>
                <p className="text-xs text-muted-foreground">Active Shifts</p>
              </CardContent></Card>
              <Card className="glass-card border-border"><CardContent className="p-4 text-center">
                <p className="text-2xl font-bold text-[#A8E6CF]">{careReports.length}</p>
                <p className="text-xs text-muted-foreground">Care Reports</p>
              </CardContent></Card>
              <Card className="glass-card border-border"><CardContent className="p-4 text-center">
                <p className="text-2xl font-bold text-amber-400">{schedule.length}</p>
                <p className="text-xs text-muted-foreground">Upcoming Shifts</p>
              </CardContent></Card>
              <Card className="glass-card border-border"><CardContent className="p-4 text-center">
                <p className="text-2xl font-bold text-[#FF6F61]">{photos.length}</p>
                <p className="text-xs text-muted-foreground">Photos</p>
              </CardContent></Card>
            </div>

            {/* AI Daily Summary Preview */}
            {dailySummaries.length > 0 && (
              <Card className="glass-card border-[#00A6A0]/20 cursor-pointer hover:border-[#00A6A0]/40 transition-colors"
                onClick={() => setActiveTab('ai-summary')} data-testid="overview-summary-preview">
                <CardContent className="p-4">
                  <div className="flex items-start gap-3">
                    <div className="p-2 rounded-xl bg-[#00A6A0]/10 shrink-0">
                      <Sparkles className="w-5 h-5 text-[#00A6A0]" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between mb-1">
                        <p className="text-sm text-foreground font-medium">Today's AI Care Summary</p>
                        <span className="text-[10px] text-muted-foreground">{dailySummaries[0].date}</span>
                      </div>
                      <p className="text-xs text-muted-foreground line-clamp-2">{dailySummaries[0].summary}</p>
                      <p className="text-[10px] text-[#00A6A0] mt-2 flex items-center gap-1">
                        View all summaries <ChevronRight className="w-3 h-3" />
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}


            {/* Recent care reports */}
            <Card className="glass-card border-border">
              <CardHeader className="border-b border-border">
                <CardTitle className="text-lg font-heading text-foreground flex items-center gap-2">
                  <FileText className="w-5 h-5 text-[#00A6A0]" /> Recent Care Reports
                </CardTitle>
              </CardHeader>
              <CardContent className="p-0">
                {careReports.length === 0 ? (
                  <div className="p-6 text-center"><p className="text-muted-foreground text-sm">No care reports yet</p></div>
                ) : (
                  <div className="divide-y divide-border">
                    {careReports.slice(0, 5).map(r => (
                      <div key={r.id} className="p-4 hover:bg-muted/30 transition-colors">
                        <div className="flex justify-between items-start">
                          <div>
                            <p className="text-sm text-foreground font-medium">{r.caregiver_name}</p>
                            <p className="text-xs text-muted-foreground">{r.shift_date}</p>
                          </div>
                          {r.signed && <CheckCircle2 className="w-4 h-4 text-[#A8E6CF]" />}
                        </div>
                        <p className="text-xs text-muted-foreground mt-1 line-clamp-2">{r.content || r.summary || ''}</p>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        )}

        {/* VISIT TIMELINE TAB */}
        {activeTab === 'visits' && (
          <div className="space-y-4" data-testid="visit-timeline-tab">
            {/* Stats */}
            <div className="grid grid-cols-3 gap-3">
              <Card className="glass-card border-border">
                <CardContent className="p-3 text-center">
                  <p className="text-xl font-bold text-foreground">{visitStats.total_visits || 0}</p>
                  <p className="text-[10px] text-muted-foreground">Total Visits</p>
                </CardContent>
              </Card>
              <Card className="glass-card border-[#00A6A0]/30">
                <CardContent className="p-3 text-center">
                  <div className="flex items-center justify-center gap-1 mb-0.5">
                    <Navigation className="w-3 h-3 text-[#00A6A0]" />
                    <p className="text-xl font-bold text-[#00A6A0]">{visitStats.gps_verified || 0}</p>
                  </div>
                  <p className="text-[10px] text-muted-foreground">GPS Verified</p>
                </CardContent>
              </Card>
              <Card className="glass-card border-[#8B5CF6]/30">
                <CardContent className="p-3 text-center">
                  <div className="flex items-center justify-center gap-1 mb-0.5">
                    <Smartphone className="w-3 h-3 text-[#8B5CF6]" />
                    <p className="text-xl font-bold text-[#8B5CF6]">{visitStats.nfc_verified || 0}</p>
                  </div>
                  <p className="text-[10px] text-muted-foreground">NFC Verified</p>
                </CardContent>
              </Card>
            </div>

            {/* Info Card */}
            <Card className="glass-card border-[#00A6A0]/20">
              <CardContent className="p-3">
                <div className="flex items-start gap-2">
                  <CheckCircle2 className="w-4 h-4 text-[#00A6A0] mt-0.5 flex-shrink-0" />
                  <p className="text-xs text-muted-foreground">Every caregiver visit is verified with GPS location and timestamped. You can see exactly when caregivers arrive and depart.</p>
                </div>
              </CardContent>
            </Card>

            {/* Timeline */}
            {visitTimeline.length === 0 ? (
              <div className="text-center py-12">
                <MapPin className="w-12 h-12 text-muted-foreground mx-auto mb-3 opacity-30" />
                <p className="text-muted-foreground">No visit records yet</p>
                <p className="text-xs text-muted-foreground mt-1">Visit events appear here when caregivers clock in and out</p>
              </div>
            ) : (
              <div className="space-y-6">
                {visitTimeline.map(day => (
                  <div key={day.date} data-testid={`timeline-day-${day.date}`}>
                    {/* Date header */}
                    <div className="flex items-center gap-3 mb-3">
                      <div className="w-10 h-10 rounded-xl bg-[#00A6A0]/10 flex items-center justify-center flex-shrink-0">
                        <Calendar className="w-5 h-5 text-[#00A6A0]" />
                      </div>
                      <div>
                        <p className="text-sm font-medium text-foreground">{new Date(day.date + 'T00:00:00').toLocaleDateString('en-GB', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}</p>
                        <p className="text-[10px] text-muted-foreground">{day.events.length} event{day.events.length !== 1 ? 's' : ''}</p>
                      </div>
                    </div>

                    {/* Events for this day */}
                    <div className="ml-5 border-l-2 border-border pl-4 space-y-3">
                      {day.events.map((ev, i) => {
                        const isIn = ev.event_type === 'check_in';
                        const dist = ev.distance_from_client_meters;
                        const method = ev.verification_method;
                        return (
                          <div key={ev.id || i} className="relative" data-testid={`timeline-event-${ev.id || i}`}>
                            {/* Dot on timeline */}
                            <div className={`absolute -left-[21px] top-1 w-3 h-3 rounded-full border-2 border-background ${isIn ? 'bg-[#A8E6CF]' : 'bg-blue-400'}`} />

                            <Card className="glass-card border-border">
                              <CardContent className="p-3">
                                <div className="flex items-center justify-between gap-2">
                                  <div className="flex items-center gap-2 flex-1 min-w-0">
                                    <span className={`px-2 py-0.5 rounded-md text-[10px] font-medium whitespace-nowrap ${isIn ? 'bg-[#A8E6CF]/10 text-[#A8E6CF]' : 'bg-blue-500/10 text-blue-400'}`}>
                                      {isIn ? 'ARRIVED' : 'DEPARTED'}
                                    </span>
                                    <span className="text-sm text-foreground font-medium truncate">{ev.caregiver_name}</span>
                                  </div>
                                  <span className="text-xs text-muted-foreground whitespace-nowrap flex items-center gap-1">
                                    <Clock className="w-3 h-3" />
                                    {new Date(ev.timestamp).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}
                                  </span>
                                </div>

                                {/* Verification badges */}
                                <div className="flex items-center gap-3 mt-2 flex-wrap">
                                  {method === 'gps' && (
                                    <span className="text-[10px] text-[#00A6A0] flex items-center gap-0.5">
                                      <Navigation className="w-3 h-3" />GPS verified
                                    </span>
                                  )}
                                  {method === 'nfc' && (
                                    <span className="text-[10px] text-[#8B5CF6] flex items-center gap-0.5">
                                      <Smartphone className="w-3 h-3" />NFC verified
                                    </span>
                                  )}
                                  {dist != null && (
                                    <span className={`text-[10px] flex items-center gap-0.5 ${dist <= 200 ? 'text-[#A8E6CF]' : 'text-amber-400'}`}>
                                      {dist <= 200 ? <CheckCircle2 className="w-3 h-3" /> : <AlertTriangle className="w-3 h-3" />}
                                      {Math.round(dist)}m from home
                                    </span>
                                  )}
                                  {ev.nfc_tag_id && (
                                    <span className="text-[10px] text-[#8B5CF6]">Tag: {ev.nfc_tag_id}</span>
                                  )}
                                </div>

                                {ev.gps_location && (
                                  <p className="text-[10px] text-muted-foreground mt-1 font-mono">
                                    {ev.gps_location.latitude?.toFixed(4)}, {ev.gps_location.longitude?.toFixed(4)}
                                    {ev.gps_location.accuracy ? ` (acc: ${Math.round(ev.gps_location.accuracy)}m)` : ''}
                                  </p>
                                )}
                              </CardContent>
                            </Card>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* AI DAILY SUMMARY TAB */}
        {activeTab === 'ai-summary' && (
          <div className="space-y-4">
            {/* Latest Summary Highlight */}
            {dailySummaries.length > 0 && (
              <Card className="glass-card border-[#00A6A0]/30 relative overflow-hidden" data-testid="latest-summary-card">
                <div className="absolute inset-0 bg-gradient-to-br from-[#00A6A0]/5 to-transparent pointer-events-none" />
                <CardHeader className="border-b border-border relative">
                  <CardTitle className="text-lg font-heading text-foreground flex items-center gap-2">
                    <Sparkles className="w-5 h-5 text-[#00A6A0]" /> Latest Daily Summary
                  </CardTitle>
                  <p className="text-xs text-muted-foreground mt-1">
                    {dailySummaries[0].date} &bull; {dailySummaries[0].report_count} report(s) summarized
                  </p>
                </CardHeader>
                <CardContent className="p-5 relative">
                  <p className="text-sm text-foreground leading-relaxed whitespace-pre-wrap" data-testid="latest-summary-text">
                    {dailySummaries[0].summary}
                  </p>
                  <div className="mt-4 flex items-center gap-2 text-[10px] text-muted-foreground">
                    <Sparkles className="w-3 h-3 text-[#00A6A0]" />
                    <span>AI-generated summary &bull; {dailySummaries[0].generated_at ? new Date(dailySummaries[0].generated_at).toLocaleString() : ''}</span>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Empty State */}
            {dailySummaries.length === 0 && (
              <Card className="glass-card border-border" data-testid="no-summaries-card">
                <CardContent className="p-10 text-center">
                  <Sparkles className="w-10 h-10 text-muted-foreground mx-auto mb-3 opacity-40" />
                  <p className="text-foreground font-medium mb-1">No Daily Summaries Yet</p>
                  <p className="text-xs text-muted-foreground max-w-sm mx-auto">
                    AI-powered daily care summaries will appear here each evening, giving you a concise overview of your loved one's day.
                  </p>
                </CardContent>
              </Card>
            )}

            {/* Past Summaries */}
            {dailySummaries.length > 1 && (
              <Card className="glass-card border-border">
                <CardHeader className="border-b border-border">
                  <CardTitle className="text-sm font-heading text-foreground flex items-center gap-2">
                    <Clock className="w-4 h-4 text-muted-foreground" /> Past Summaries
                  </CardTitle>
                </CardHeader>
                <CardContent className="p-0">
                  <div className="divide-y divide-border">
                    {dailySummaries.slice(1).map(s => (
                      <details key={s.id} className="group" data-testid={`summary-${s.id}`}>
                        <summary className="p-4 cursor-pointer hover:bg-muted/30 transition-colors flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            <div className="p-1.5 rounded-lg bg-[#00A6A0]/10">
                              <FileText className="w-3.5 h-3.5 text-[#00A6A0]" />
                            </div>
                            <div>
                              <p className="text-sm text-foreground font-medium">{s.date}</p>
                              <p className="text-[10px] text-muted-foreground">{s.report_count} report(s)</p>
                            </div>
                          </div>
                          <ChevronRight className="w-4 h-4 text-muted-foreground group-open:rotate-90 transition-transform" />
                        </summary>
                        <div className="px-4 pb-4 pl-12">
                          <p className="text-xs text-muted-foreground leading-relaxed whitespace-pre-wrap">{s.summary}</p>
                          <p className="text-[10px] text-muted-foreground mt-2">
                            Generated: {s.generated_at ? new Date(s.generated_at).toLocaleString() : 'N/A'}
                          </p>
                        </div>
                      </details>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        )}


        {/* CARE TRACKING TAB */}
        {activeTab === 'tracking' && (
          <div className="space-y-4">
            <Card className="glass-card border-border overflow-hidden">
              <div ref={mapRef} style={{ height: '280px' }} data-testid="family-tracking-map" />
            </Card>
            {tracking.active_shifts.length === 0 ? (
              <Card className="glass-card border-border"><CardContent className="p-6 text-center"><p className="text-muted-foreground">No active shifts today</p></CardContent></Card>
            ) : (
              tracking.active_shifts.map(s => (
                <Card key={s.id} className="glass-card border-border" data-testid={`fam-shift-${s.id}`}>
                  <CardContent className="p-4 flex items-center gap-4">
                    <div className={`p-2 rounded-xl ${s.status === 'in_progress' ? 'bg-[#A8E6CF]/10' : 'bg-blue-400/10'}`}>
                      {s.status === 'in_progress' ? <Radio className="w-5 h-5 text-[#A8E6CF]" /> : <Clock className="w-5 h-5 text-blue-400" />}
                    </div>
                    <div className="flex-1">
                      <p className="text-foreground font-medium">{s.caregiver_name}</p>
                      <p className="text-xs text-muted-foreground">{s.start_time} - {s.end_time}</p>
                      <div className="flex gap-3 mt-2">
                        <span className={`text-[10px] px-2 py-0.5 rounded-full ${s.check_in ? 'bg-[#A8E6CF]/10 text-[#A8E6CF]' : 'bg-muted text-muted-foreground'}`}>
                          {s.check_in ? 'Checked In' : 'Awaiting'}
                        </span>
                        <span className={`text-[10px] px-2 py-0.5 rounded-full ${s.care_report_submitted ? 'bg-[#00A6A0]/10 text-[#00A6A0]' : 'bg-muted text-muted-foreground'}`}>
                          {s.care_report_submitted ? 'Report Done' : 'No Report'}
                        </span>
                      </div>
                    </div>
                    {s.live_location && <Navigation className="w-4 h-4 text-[#FF6F61] animate-pulse" />}
                  </CardContent>
                </Card>
              ))
            )}
          </div>
        )}

        {/* PHOTOS TAB */}
        {activeTab === 'photos' && (
          <div>
            {photos.length === 0 ? (
              <Card className="glass-card border-border"><CardContent className="p-8 text-center"><Image className="w-10 h-10 text-muted-foreground mx-auto mb-3" /><p className="text-muted-foreground">No photos yet</p></CardContent></Card>
            ) : (
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
                {photos.map(p => (
                  <Card key={p.id} className="glass-card border-border overflow-hidden">
                    <div className="aspect-square"><img src={p.photo_data || p.photo_url} alt={p.title || ''} className="w-full h-full object-cover" /></div>
                    {p.title && <CardContent className="p-2"><p className="text-xs text-muted-foreground truncate">{p.title}</p></CardContent>}
                  </Card>
                ))}
              </div>
            )}
          </div>
        )}

        {/* CARE REPORTS TAB */}
        {activeTab === 'reports' && (
          <Card className="glass-card border-border">
            <CardHeader className="border-b border-border"><CardTitle className="text-lg font-heading text-foreground">All Care Reports</CardTitle></CardHeader>
            <CardContent className="p-0">
              {careReports.length === 0 ? (
                <div className="p-8 text-center"><p className="text-muted-foreground">No care reports</p></div>
              ) : (
                <div className="divide-y divide-border">
                  {careReports.map(r => (
                    <div key={r.id} className="p-4">
                      <div className="flex justify-between"><p className="text-foreground font-medium text-sm">{r.caregiver_name}</p><p className="text-xs text-muted-foreground">{r.shift_date}</p></div>
                      <p className="text-xs text-muted-foreground mt-1">{r.content || r.summary || 'No content'}</p>
                      {r.signed && <span className="inline-block mt-1 text-[10px] px-2 py-0.5 rounded-full bg-[#A8E6CF]/10 text-[#A8E6CF]">Signed</span>}
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* MESSAGES TAB */}
        {activeTab === 'messages' && (
          <div className="grid lg:grid-cols-3 gap-4">
            {/* Contacts */}
            <Card className="glass-card border-border lg:col-span-1">
              <CardHeader className="border-b border-border"><CardTitle className="text-sm font-heading text-foreground">Contacts</CardTitle></CardHeader>
              <CardContent className="p-0">
                {contacts.length === 0 ? (
                  <div className="p-4 text-center"><p className="text-xs text-muted-foreground">No contacts available</p></div>
                ) : (
                  <div className="divide-y divide-border">
                    {contacts.map(c => (
                      <button key={c.id} onClick={() => { setSelectedContact(c); fetchMessages(c.id); }}
                        className={`w-full p-3 flex items-center gap-3 text-left hover:bg-muted/30 transition-colors ${selectedContact?.id === c.id ? 'bg-[#00A6A0]/5 border-l-2 border-l-[#00A6A0]' : ''}`}
                        data-testid={`contact-${c.id}`}>
                        <div className="w-8 h-8 rounded-full bg-[#00A6A0]/10 flex items-center justify-center overflow-hidden">
                          {c.profile_photo_url ? <img src={c.profile_photo_url} className="w-full h-full object-cover" alt="" /> : <User className="w-4 h-4 text-[#00A6A0]" />}
                        </div>
                        <div>
                          <p className="text-sm text-foreground font-medium">{c.name}</p>
                          <p className="text-[10px] text-muted-foreground capitalize">{c.role?.replace('_', ' ')}</p>
                        </div>
                      </button>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Chat */}
            <Card className="glass-card border-border lg:col-span-2">
              <CardHeader className="border-b border-border">
                <CardTitle className="text-sm font-heading text-foreground">
                  {selectedContact ? `Chat with ${selectedContact.name}` : 'Select a contact'}
                </CardTitle>
              </CardHeader>
              <CardContent className="p-0">
                {selectedContact ? (
                  <>
                    <div className="h-64 overflow-y-auto p-4 space-y-2">
                      {messages.length === 0 ? (
                        <p className="text-xs text-muted-foreground text-center py-8">No messages yet</p>
                      ) : (
                        [...messages].reverse().map(m => (
                          <div key={m.id} className={`flex ${m.sender_id === user?.id ? 'justify-end' : 'justify-start'}`}>
                            <div className={`max-w-[70%] rounded-xl px-3 py-2 text-xs ${
                              m.sender_id === user?.id ? 'bg-[#00A6A0] text-white' : 'bg-muted text-foreground'
                            }`}>
                              <p>{m.content}</p>
                              <p className={`text-[9px] mt-1 ${m.sender_id === user?.id ? 'text-white/60' : 'text-muted-foreground'}`}>
                                {m.created_at ? new Date(m.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : ''}
                              </p>
                            </div>
                          </div>
                        ))
                      )}
                    </div>
                    <div className="p-3 border-t border-border flex gap-2">
                      <SpeechToTextButton compact onTranscript={text => setMsgText(prev => prev ? prev + ' ' + text : text)} />
                      <Input value={msgText} onChange={e => setMsgText(e.target.value)}
                        placeholder="Type a message..." className="bg-muted border-border flex-1"
                        onKeyDown={e => { if (e.key === 'Enter') sendMessage(); }}
                        data-testid="family-msg-input" />
                      <Button onClick={sendMessage} className="btn-primary" data-testid="family-send-btn">
                        <Send className="w-4 h-4" />
                      </Button>
                    </div>
                  </>
                ) : (
                  <div className="p-8 text-center"><MessageSquare className="w-8 h-8 text-muted-foreground mx-auto mb-2" /><p className="text-xs text-muted-foreground">Select a contact to start chatting</p></div>
                )}
              </CardContent>
            </Card>
          </div>
        )}

        {/* SCHEDULE TAB */}
        {activeTab === 'schedule' && (
          <Card className="glass-card border-border">
            <CardHeader className="border-b border-border"><CardTitle className="text-lg font-heading text-foreground flex items-center gap-2"><Calendar className="w-5 h-5 text-[#00A6A0]" /> Upcoming Shifts</CardTitle></CardHeader>
            <CardContent className="p-0">
              {schedule.length === 0 ? (
                <div className="p-8 text-center"><p className="text-muted-foreground">No upcoming shifts</p></div>
              ) : (
                <div className="divide-y divide-border">
                  {schedule.map(s => (
                    <div key={s.id} className="p-4 flex items-center gap-4">
                      <div className="p-2 rounded-lg bg-[#00A6A0]/10"><Calendar className="w-4 h-4 text-[#00A6A0]" /></div>
                      <div className="flex-1">
                        <p className="text-foreground font-medium text-sm">{s.caregiver_name || 'Unassigned'}</p>
                        <p className="text-xs text-muted-foreground">{s.date} &bull; {s.start_time} - {s.end_time}</p>
                      </div>
                      <span className={`text-[10px] px-2 py-0.5 rounded-full ${s.status === 'in_progress' ? 'bg-[#A8E6CF]/10 text-[#A8E6CF]' : 'bg-blue-400/10 text-blue-400'}`}>{s.status}</span>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* MODIFICATIONS TAB */}
        {activeTab === 'modifications' && (
          <div className="space-y-4">
            <Card className="glass-card border-border">
              <CardHeader className="border-b border-border"><CardTitle className="text-lg font-heading text-foreground flex items-center gap-2"><Edit3 className="w-5 h-5 text-[#FF6F61]" /> Request Shift Modification</CardTitle></CardHeader>
              <CardContent className="p-4 space-y-3">
                <div className="grid sm:grid-cols-2 gap-3">
                  <div>
                    <label className="text-xs text-muted-foreground block mb-1">Select Shift</label>
                    <select value={modForm.shift_id} onChange={e => setModForm({...modForm, shift_id: e.target.value})}
                      className="w-full rounded-xl border border-border bg-muted p-2 text-sm text-foreground" data-testid="mod-shift-select">
                      <option value="">Select a shift...</option>
                      {schedule.map(s => <option key={s.id} value={s.id}>{s.date} {s.start_time}-{s.end_time} ({s.caregiver_name})</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="text-xs text-muted-foreground block mb-1">Request Type</label>
                    <select value={modForm.request_type} onChange={e => setModForm({...modForm, request_type: e.target.value})}
                      className="w-full rounded-xl border border-border bg-muted p-2 text-sm text-foreground">
                      <option value="modification">Change Hours</option>
                      <option value="cancellation">Cancel Shift</option>
                      <option value="additional">Request Additional Hours</option>
                    </select>
                  </div>
                  <Input placeholder="Current time" value={modForm.current_time} onChange={e => setModForm({...modForm, current_time: e.target.value})} className="bg-muted border-border" />
                  <Input placeholder="Requested time" value={modForm.requested_time} onChange={e => setModForm({...modForm, requested_time: e.target.value})} className="bg-muted border-border" />
                </div>
                <Textarea placeholder="Reason for request..." value={modForm.reason} onChange={e => setModForm({...modForm, reason: e.target.value})}
                  className="bg-muted border-border" rows={3} data-testid="mod-reason" />
                <div className="flex justify-end">
                  <Button onClick={submitModRequest} className="btn-primary gap-2" data-testid="submit-mod-btn"><Send className="w-4 h-4" /> Submit Request</Button>
                </div>
              </CardContent>
            </Card>

            {/* Past requests */}
            {modRequests.length > 0 && (
              <Card className="glass-card border-border">
                <CardHeader className="border-b border-border"><CardTitle className="text-sm font-heading text-foreground">Past Requests</CardTitle></CardHeader>
                <CardContent className="p-0">
                  <div className="divide-y divide-border">
                    {modRequests.map(r => (
                      <div key={r.id} className="p-4">
                        <div className="flex justify-between"><p className="text-sm text-foreground font-medium capitalize">{r.request_type}</p>
                          <span className={`text-[10px] px-2 py-0.5 rounded-full ${r.status === 'approved' ? 'bg-[#A8E6CF]/10 text-[#A8E6CF]' : r.status === 'rejected' ? 'bg-red-400/10 text-red-400' : 'bg-amber-400/10 text-amber-400'}`}>{r.status}</span>
                        </div>
                        <p className="text-xs text-muted-foreground mt-1">{r.reason}</p>
                        <p className="text-[10px] text-muted-foreground mt-1">{r.created_at ? new Date(r.created_at).toLocaleDateString() : ''}</p>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        )}

        {/* Video Call Link */}
        <Card className="glass-card border-[#00A6A0]/30">
          <CardContent className="p-4 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-[#00A6A0]/10"><Video className="w-5 h-5 text-[#00A6A0]" /></div>
              <div>
                <p className="text-foreground font-medium text-sm">Video Call</p>
                <p className="text-xs text-muted-foreground">Connect with your client or caregiver</p>
              </div>
            </div>
            <Link to="/video-call">
              <Button className="btn-primary gap-2" data-testid="family-video-call-btn"><Video className="w-4 h-4" /> Start Call</Button>
            </Link>
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
}

export default FamilyDashboard;
